import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, errMsg } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading, null = logged out, object = logged in
    const [error, setError] = useState("");

    const bootstrap = useCallback(async () => {
        try {
            const { data } = await http.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    const login = async (email, password, totpCode = null) => {
        setError("");
        try {
            const payload = { email, password };
            if (totpCode) payload.totp_code = totpCode;
            const { data } = await http.post("/auth/login", payload);
            // Backend signals "TOTP required" with a 200 + flag instead of
            // a 401 so the UI can branch into the 2FA prompt without
            // treating the password as wrong.
            if (data?.requires_totp) {
                return { requires_totp: true, email: data.email };
            }
            // Refresh full user profile with tenant
            const me = await http.get("/auth/me");
            setUser(me.data);
            return data;
        } catch (e) {
            const msg = errMsg(e);
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (payload) => {
        setError("");
        try {
            const r = await http.post("/auth/register", payload);
            const me = await http.get("/auth/me");
            setUser(me.data);
            return r.data; // expose plan + requires_payment to the caller
        } catch (e) {
            const msg = errMsg(e);
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try {
            await http.post("/auth/logout");
        } catch {
            /* ignore */
        }
        setUser(null);
    };

    return (
        <AuthCtx.Provider value={{ user, login, register, logout, error, setError, reload: bootstrap }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
